import type { SgWorkspace } from "./api/types.ts";
import { queryOptions } from "./api/client.ts";
import { fetchSession } from "./api/routes/session.ts";
import { fetchWorkspaces } from "./api/routes/subscription.ts";
import { fetchTenants } from "./api/routes/tenant.ts";
import { PageLoader, ToastProvider } from "@sg-protect/components";
import { navigateTo, tenantIdStorage } from "@sg-protect/core";
import {
    AppRouter as SquideAppRouter,
    useDeferredRegistrations,
    useIsBootstrapping,
    useProtectedDataQueries
} from "@sg-protect/federation-runtime/squide";
import config from "@sg-protect/runtime-config";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { MotionConfig } from "motion/react";
import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { RouterProvider as RACRouterProvider } from "react-aria-components";
import { Outlet, createBrowserRouter, useNavigate } from "react-router";
import { RouterProvider } from "react-router/dom";
import { AppRouterErrorBoundary } from "./AppRouterErrorBoundary.tsx";
import { AutomaticThemeProvider } from "./automatic-theme-provider/AutomaticThemeProvider.tsx";
import { DevTools } from "./dev-tools/DevTools.tsx";
import { useShellTranslation } from "./i18n/i18next.ts";
import { shouldShowDevTools } from "./dev-tools/shouldShowDevTools.ts";
import type { DeferredRegistrationData } from "./moduleRegisterFunction.ts";
import { TrialReasonGate } from "./onboarding/TrialReasonGate.tsx";
import { SessionProvider, sessionStore } from "./SessionProvider.tsx";
import { useCurrentWorkspace } from "./useCurrentWorkspace.ts";
import { useWorkspaceRegion } from "./useWorkspaceRegion.ts";
import { type TenantsContextType, TenantsProvider } from "./TenantsContext.tsx";
import { type WorkspaceContextType, WorkspaceProvider } from "./WorkspaceContext.tsx";

function createQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false
            }
        }
    });
}

const workspacesQueryKey = queryOptions("get", "/bff/subscription/workspaces").queryKey;

const bootstrapWorkspacesQuery = {
    queryKey: workspacesQueryKey,
    queryFn: () =>
        fetchWorkspaces().then(data => {
            if (data?.workspaces.length === 0) {
                // Some old users might not have access to any workspace, in that case we redirect them to home page
                navigateTo(config.get().home.baseUrl);
            }

            return data?.workspaces;
        })
};

function tenantsQueryKey(workspaceId: string | undefined) {
    return [...queryOptions("get", "/bff/tenants").queryKey, workspaceId];
}

// The header mounts before the module providers that set the tenant, and a request without the tenant
// reads the workspace as not consented, so the tenant is set here, before anything renders.
async function loadTenants(workspaceId: string | undefined) {
    const tenants = await fetchTenants(workspaceId);
    tenantIdStorage.set(tenants.primaryTenant?.id ?? null);

    return tenants;
}

function BootstrappingRoute() {
    const { t } = useShellTranslation();
    const [workspaces, setWorkspaces] = useState<SgWorkspace[]>([]);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { currentWorkspace, changeWorkspace } = useCurrentWorkspace(workspaces);
    const [workspacesResponse, session, tenants] = useProtectedDataQueries(
        [
            bootstrapWorkspacesQuery,
            {
                queryKey: queryOptions("get", "/bff/session").queryKey,
                queryFn: () =>
                    fetchSession(currentWorkspace?.id).then(data => {
                        // Seed module-level store so non-React consumers (route loaders) can read
                        // the session synchronously before BootstrappingRoute renders.
                        sessionStore.setSession(data);

                        return data;
                    }),
                enabled: !!currentWorkspace
            },
            {
                queryKey: tenantsQueryKey(currentWorkspace?.id),
                queryFn: () => loadTenants(currentWorkspace?.id),
                enabled: !!currentWorkspace,
                // Only setup changes it, and refetches it through refetchTenants.
                staleTime: Infinity
            }
        ],
        () => false
    );

    useEffect(() => {
        void queryClient.invalidateQueries({ queryKey: queryOptions("get", "/bff/session").queryKey });
    }, [queryClient, currentWorkspace]);

    useEffect(() => {
        if (workspacesResponse) {
            setWorkspaces(workspacesResponse);
        }
    }, [workspacesResponse]);

    useEffect(() => {
        function handlePageShow(event: PageTransitionEvent) {
            // Back/forward restores this page without running the bootstrap again, and the page shown in
            // between may have stored its own workspace's tenant.
            if (event.persisted && tenants) {
                tenantIdStorage.set(tenants.primaryTenant?.id ?? null);
            }
        }

        window.addEventListener("pageshow", handlePageShow);

        return () => window.removeEventListener("pageshow", handlePageShow);
    }, [tenants]);

    const refreshWorkspaces = useCallback(async () => {
        const refreshed =
            (await queryClient.fetchQuery({
                queryKey: workspacesQueryKey,
                queryFn: () => fetchWorkspaces().then(data => data?.workspaces),
                staleTime: 0
            })) ?? [];

        // An empty list here is transient, not the no-access case the bootstrap redirect handles.
        if (refreshed.length > 0) {
            setWorkspaces(refreshed);
        }

        return refreshed;
    }, [queryClient]);

    const data: DeferredRegistrationData = useMemo(
        () => ({
            workspaces,
            currentWorkspace,
            session
        }),
        [currentWorkspace, session, workspaces]
    );

    const workspaceContext: WorkspaceContextType | null = useMemo(() => {
        if (!currentWorkspace) {
            return null;
        }

        return {
            currentWorkspace,
            workspaces,
            changeWorkspace,
            refreshWorkspaces
        };
    }, [changeWorkspace, currentWorkspace, refreshWorkspaces, workspaces]);

    const refetchTenants = useCallback(
        () =>
            queryClient.refetchQueries(
                { queryKey: tenantsQueryKey(currentWorkspace?.id) },
                { throwOnError: true, cancelRefetch: false }
            ),
        [queryClient, currentWorkspace?.id]
    );

    const tenantsContext: TenantsContextType = useMemo(
        () => ({ tenants: tenants ?? null, refetchTenants }),
        [tenants, refetchTenants]
    );

    useDeferredRegistrations(data);

    const isRegionReloading = useWorkspaceRegion(tenants);

    if (useIsBootstrapping() || isRegionReloading) {
        return <PageLoader height="100dvh" width="100dvw" aria-label={t("Loaders.loading")} />;
    }

    const protectSession = session ?? null;

    return (
        <SessionProvider value={protectSession}>
            <WorkspaceProvider value={workspaceContext ?? null}>
                <TenantsProvider value={tenantsContext}>
                    <RACRouterProvider navigate={navigate}>
                        {protectSession ? (
                            <TrialReasonGate>
                                <Outlet />
                            </TrialReasonGate>
                        ) : (
                            <Outlet />
                        )}
                        {shouldShowDevTools && (
                            <Suspense fallback={null}>
                                <DevTools />
                            </Suspense>
                        )}
                    </RACRouterProvider>
                </TenantsProvider>
            </WorkspaceProvider>
        </SessionProvider>
    );
}

export function AppRouter() {
    const [queryClient] = useState(createQueryClient);

    return (
        <QueryClientProvider client={queryClient}>
            <AutomaticThemeProvider display="flex" flexDirection="column" flex="1 0 auto">
                <MotionConfig reducedMotion="user">
                    <ToastProvider>
                        <SquideAppRouter waitForProtectedData>
                            {({ rootRoute, registeredRoutes, routerProps, routerProviderProps }) => (
                                <RouterProvider
                                    router={createBrowserRouter(
                                        [
                                            {
                                                element: rootRoute as ReactNode,
                                                errorElement: <AppRouterErrorBoundary />,
                                                children: [
                                                    {
                                                        element: <BootstrappingRoute />,
                                                        children: registeredRoutes
                                                    }
                                                ]
                                            }
                                        ],
                                        routerProps
                                    )}
                                    {...routerProviderProps}
                                />
                            )}
                        </SquideAppRouter>
                    </ToastProvider>
                </MotionConfig>
            </AutomaticThemeProvider>
        </QueryClientProvider>
    );
}
